/**
 * Auth cookie helpers for unified auth across botblitz.site and subdomains (e.g. bot.botblitz.site).
 * Cookies are set with domain .botblitz.site so the landing page and all subdomains can read them.
 */
import Cookies from 'js-cookie';
import { getDomainName } from './brand/brand';
import { getAppId } from './config/config';

const AUTH_TOKEN = 'authToken';
const ACTIVE_LOGINID = 'active_loginid';
const ACCOUNTS_LIST = 'accountsList';
const CLIENT_ACCOUNTS = 'clientAccounts';
const APP_ID_KEY = 'auth_app_id';

const getWildcardDomain = (): string => {
    const domain = getDomainName();
    return domain ? `.${domain}` : '';
};

const getCookieOptions = (): Cookies.CookieAttributes => {
    const domain = getWildcardDomain();
    return {
        path: '/',
        domain,
        secure: typeof window !== 'undefined' && window.location?.protocol === 'https:',
        sameSite: 'Lax',
    };
};

export const getAuthCookies = (): {
    authToken: string | undefined;
    active_loginid: string | undefined;
    accountsList: Record<string, string> | null;
    clientAccounts: Record<string, { loginid: string; token: string; currency: string }> | null;
    app_id: string | undefined;
} => {
    try {
        const authToken = Cookies.get(AUTH_TOKEN);
        const active_loginid = Cookies.get(ACTIVE_LOGINID);
        const app_id = Cookies.get(APP_ID_KEY);
        let accountsList: Record<string, string> | null = null;
        let clientAccounts: Record<string, { loginid: string; token: string; currency: string }> | null = null;

        const accountsListStr = Cookies.get(ACCOUNTS_LIST);
        if (accountsListStr) {
            try {
                accountsList = JSON.parse(accountsListStr) as Record<string, string>;
            } catch {
                // ignore
            }
        }
        const clientAccountsStr = Cookies.get(CLIENT_ACCOUNTS);
        if (clientAccountsStr) {
            try {
                clientAccounts = JSON.parse(clientAccountsStr) as Record<
                    string,
                    { loginid: string; token: string; currency: string }
                >;
            } catch {
                // ignore
            }
        }

        return { authToken, active_loginid, accountsList, clientAccounts, app_id };
    } catch (error) {
        console.error('Error getting auth cookies:', error);
        return {
            authToken: undefined,
            active_loginid: undefined,
            accountsList: null,
            clientAccounts: null,
            app_id: undefined,
        };
    }
};

/**
 * If auth cookies exist (set by botblitz.site callback), populate localStorage so the app
 * and subdomains see the user as logged in. Call this at app init before client-store reads storage.
 */
export const syncAuthFromCookiesToLocalStorage = (): boolean => {
    try {
        const { authToken, active_loginid, accountsList, clientAccounts, app_id } = getAuthCookies();
        
        if (
            !authToken ||
            !active_loginid ||
            !accountsList ||
            Object.keys(accountsList).length === 0
        ) {
            return false;
        }
        
        const current_app_id = getAppId();
        if (app_id && app_id !== current_app_id) {
            // eslint-disable-next-line no-console
            console.log('[dtrader-c][auth-cookie-utils] syncAuthFromCookiesToLocalStorage: app_id mismatch', {
                cookie_app_id: app_id,
                current_app_id,
            });
            return false;
        }
        
        localStorage.setItem('authToken', authToken);
        localStorage.setItem('active_loginid', active_loginid);
        localStorage.setItem('accountsList', JSON.stringify(accountsList));
        if (clientAccounts && Object.keys(clientAccounts).length > 0) {
            localStorage.setItem('clientAccounts', JSON.stringify(clientAccounts));
            localStorage.setItem('client.accounts', JSON.stringify(clientAccounts));
        }
        // eslint-disable-next-line no-console
        console.log('[dtrader-c][auth-cookie-utils] syncAuthFromCookiesToLocalStorage: applied cookies to localStorage', {
            active_loginid,
            app_id: current_app_id,
            accountsListKeys: Object.keys(accountsList || {}),
            hasClientAccounts: !!clientAccounts && Object.keys(clientAccounts).length > 0,
        });
        return true;
    } catch (error) {
        console.error('[app.botblitz] Error syncing auth from cookies to localStorage:', error);
        return false;
    }
};

export const removeAuthCookies = (): void => {
    try {
        const domain = getWildcardDomain();
        const opts = domain ? { path: '/', domain } : { path: '/' };

        Cookies.remove(AUTH_TOKEN, opts);
        Cookies.remove(ACTIVE_LOGINID, opts);
        Cookies.remove(ACCOUNTS_LIST, opts);
        Cookies.remove(CLIENT_ACCOUNTS, opts);
        Cookies.remove(APP_ID_KEY, opts);
        // Also try without domain for cleanup
        if (domain) {
            Cookies.remove(AUTH_TOKEN);
            Cookies.remove(ACTIVE_LOGINID);
            Cookies.remove(ACCOUNTS_LIST);
            Cookies.remove(CLIENT_ACCOUNTS);
            Cookies.remove(APP_ID_KEY);
        }
    } catch (error) {
        console.error('Error removing auth cookies:', error);
    }
};
